import React from "react";
import api, { API, extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Search, X } from "lucide-react";


const StatCard = ({ icon: Icon, label, value }) => (
  <div className="card-warm p-6">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[#5E5C58] text-sm">{label}</div>
        <div className="font-heading text-3xl text-[#222222] mt-1">{value}</div>
      </div>
      <div className="w-11 h-11 rounded-full bg-[#F6F4EE] grid place-items-center">
        <Icon className="w-5 h-5 text-[#0A4D3C]" />
      </div>
    </div>
  </div>
);


// Reusable cell that surfaces an invoice's number + preview/download buttons
// for a given booking_id or purchase_id row in the admin tables.
const InvoiceCell = ({ invoice }) => {
  const downloadInvoice = async () => {
    try {
      const res = await api.get(`/admin/invoices/${invoice.id}?download=1`, {
        responseType: "blob",
        timeout: 30000,
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "text/html;charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `awtadk-invoice-${invoice.invoice_number}.html`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success("بدأ تحميل الفاتورة");
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر تحميل الفاتورة"));
    }
  };

  const previewInvoice = async () => {
    try {
      const res = await api.get(`/admin/invoices/${invoice.id}`, {
        responseType: "blob",
        timeout: 30000,
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "text/html;charset=utf-8" }),
      );
      const w = window.open(url, "_blank", "noopener,noreferrer");
      // popup blockers may swallow the open() — fall back to a download anchor
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر معاينة الفاتورة"));
    }
  };

  if (!invoice) {
    return <span className="text-xs text-[#A8A29E]">—</span>;
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className="font-mono text-[11px] text-[#0A4D3C]"
        dir="ltr"
        data-testid="admin-invoice-number"
      >
        {invoice.invoice_number}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={previewInvoice}
          className="text-[11px] text-[#0A4D3C] underline-offset-2 hover:underline"
          data-testid="admin-invoice-preview-btn"
        >
          معاينة
        </button>
        <span className="text-[#D6D3CB]">·</span>
        <button
          type="button"
          onClick={downloadInvoice}
          className="text-[11px] text-[#0A4D3C] underline-offset-2 hover:underline"
          data-testid="admin-invoice-download-btn"
        >
          تحميل
        </button>
      </div>
    </div>
  );
};



// ---------------------------------------------------------------------------
// Iteration 26 — Admin search bar
// Debounced free-text search with a clear (X) button. Used above the
// Bookings / Purchases / Email-log tables. The `value` prop is the live
// input string; the parent owns `query` state which only updates after
// the debounce window so we don't hammer the API on every keystroke.
// ---------------------------------------------------------------------------
const AdminSearchBar = ({ value, onChange, placeholder, testId, resultCount }) => (
  <div className="mb-4 flex items-center gap-3">
    <div className="relative flex-1 max-w-xl">
      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B8780] pointer-events-none" />
      <Input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pr-10 pl-10 rounded-full bg-white border-[#E5E1D8] focus-visible:ring-1 focus-visible:ring-[#C5A059]"
      />
      {value && (
        <button
          type="button"
          aria-label="مسح البحث"
          data-testid={`${testId}-clear`}
          onClick={() => onChange("")}
          className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full hover:bg-[#F0EDE4] flex items-center justify-center"
        >
          <X className="h-3.5 w-3.5 text-[#5E5C58]" />
        </button>
      )}
    </div>
    {value && (
      <span className="text-xs text-[#5E5C58]" data-testid={`${testId}-count`}>
        {resultCount === 0 ? "لا نتائج" : `${resultCount} نتيجة`}
      </span>
    )}
  </div>
);



// ---------------------------------------------------------------------------
// Iteration 27 — Pagination footer (Prev / Next + "صفحة X من Y" + total).
// Page size is fixed at 20 server-side; the UI just needs Prev/Next + a
// label. Out-of-range pages are clamped by the server.
// ---------------------------------------------------------------------------
const PaginationFooter = ({ page, totalPages, total, onPrev, onNext, testId }) => {
  if (!total || total === 0) return null;
  const safeTotalPages = Math.max(1, totalPages || 1);
  return (
    <div
      className="mt-3 flex items-center justify-between gap-3 text-xs text-[#5E5C58]"
      data-testid={testId}
    >
      <div data-testid={`${testId}-summary`}>
        {`الإجمالي: ${total} سجل`}
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="rounded-full h-8 px-3"
          disabled={page <= 1}
          onClick={onPrev}
          data-testid={`${testId}-prev`}
        >
          السابق
        </Button>
        <span data-testid={`${testId}-label`} className="font-medium text-[#5E5C58]">
          {`صفحة ${page} من ${safeTotalPages}`}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full h-8 px-3"
          disabled={page >= safeTotalPages}
          onClick={onNext}
          data-testid={`${testId}-next`}
        >
          التالي
        </Button>
      </div>
    </div>
  );
};



// ---------------------------------------------------------------------------
// Iteration 28 — Loyal Customer Badge
// Inline pill rendered next to the customer's name when the server flags
// `user_is_loyal=true` (>= 3 successful bookings/purchases). Sparkles icon
// kept consistent with the rest of the lucide-based UI; the pill is sized
// to never break the table column width.
// ---------------------------------------------------------------------------
const LoyalBadge = ({ count }) => (
  <span
    className="inline-flex items-center gap-1 ms-2 align-middle px-2 py-0.5 rounded-full bg-[#FBF1DC] text-[#8A6A1F] border border-[#E8D9A8] text-[10px] font-medium leading-none whitespace-nowrap"
    title={`عميل دائم (${count} معاملة)`}
    data-testid="loyal-customer-badge"
  >
    <Sparkles className="w-3 h-3" />
    عميل دائم
  </span>
);

export { StatCard, InvoiceCell, AdminSearchBar, PaginationFooter, LoyalBadge };
